class BaseLocalStorage {
    protected readonly key: string;
    constructor(key: string) {
        this.key = key;
    }

    getRawValue(): string {
        return localStorage.getItem(this.key) ?? "";
    }

    setRawValue(stringifiedData: string): void {
        try {
            localStorage.setItem(this.key, stringifiedData);
        } catch (e) {
            console.warn("LocalStorage: Error while setting ", this.key, e);
        }
    }

    clear(): void {
        try {
            localStorage.removeItem(this.key);
        } catch (e) {
            console.warn("LocalStorage: Error while clearing ", this.key, e);
        }
    }
}

class ArrayLocalStorage<T> extends BaseLocalStorage {
    private readonly capacity: number;
    private readonly uniqueCheckFunc?: (existing: T, incoming: T) => boolean;

    constructor(key: string, capacity: number, uniqueCheckFunc?: (existing: T, incoming: T) => boolean) {
        super(key);
        this.capacity = capacity;
        this.uniqueCheckFunc = uniqueCheckFunc;
    }

    getValue(): T[] {
        const raw = this.getRawValue();
        if (!raw) return [];
        try {
            return JSON.parse(raw) as T[];
        } catch (err) {
            console.warn("LocalStorage: Error while parsing data ", this.key, err);
            return [];
        }
    }

    setValue(data: T[]): void {
        const capped = data.length > this.capacity ? data.slice(0, this.capacity) : data;
        try {
            this.setRawValue(JSON.stringify(capped));
        } catch (err) {
            console.warn("LocalStorage: Error while stringifying data ", this.key, err);
            this.setRawValue(JSON.stringify([]));
        }
    }

    push(data: T): void {
        const curr = this.getValue();

        if (this.uniqueCheckFunc) {
            const index = curr.findIndex(value => this.uniqueCheckFunc!(value, data));
            if (index !== -1) {
                curr.splice(index, 1);
            }
        }

        curr.unshift(data);

        while (curr.length > this.capacity) {
            curr.pop();
        }

        this.setValue(curr);
    }

    remove(predicate: (value: T) => boolean): boolean {
        const curr = this.getValue();
        const index = curr.findIndex(predicate);

        if (index === -1) return false;

        curr.splice(index, 1);
        this.setValue(curr);
        return true;
    }
}

const localStorageKeyMap = new Map<string, unknown>();

export function createArrayLocalStorage<T extends unknown>(
    key: string,
    capacity: number,
    uniqueCheckFunc?: (existing: T, incoming: T) => boolean
): ArrayLocalStorage<T> {
    if (localStorageKeyMap.has(key)) {
        return localStorageKeyMap.get(key) as ArrayLocalStorage<T>;
    }

    const instance = new ArrayLocalStorage(key, capacity, uniqueCheckFunc);
    localStorageKeyMap.set(key, instance);
    return instance;
}

export enum LocalStorageKeys {
    TableMappingValidationErrors = "TABLEMAPPING_VALIDATION_ERRORS",
    AppErrors = "APP_ERRORS"
}

export function purgeLegacyPrefixedKeys(prefixes: string[]): void {
    try {
        Object.keys(localStorage)
            .filter(key => prefixes.some(prefix => key.startsWith(prefix)))
            .forEach(key => localStorage.removeItem(key));
    } catch (e) {
        console.warn("LocalStorage: Error while purging legacy keys ", prefixes, e);
    }
}
